import { useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { ReactComponent as LogoIcon } from '~assets/auge-logo.svg';
import { ReactComponent as LogoIconMinimal } from '~assets/auge-ico.svg';
import { ReactComponent as SignOutIcon } from '~assets/sign_out.svg';

import {
  Background,
  BreadCrumb,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbCurrent,
  Container,
  SearchButton,
  Profile,
  Separator,
  LogoutButton,
  Kbd,
} from './styles';

import { useAuth } from '~context/auth';
import { NotificationBadge } from '../NotificationBadge';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

type Props = {
  minimal?: boolean;
  route?: string[];
  breadcrumb?: BreadcrumbItem[];
}

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

export function Header({ minimal = false, route = [''], breadcrumb }: Props) {
  const { push } = useHistory();
  const { user, avatar, signOut } = useAuth();

  const handleSearchClick = useCallback(() => {
    window.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'k',
        metaKey: true,
        bubbles: true,
      })
    );
  }, []);

  const handleLogout = useCallback(() => {
    signOut();
  }, [signOut]);

  const handleLogoutKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleLogout();
      }
    },
    [handleLogout]
  );

  const renderBreadcrumb = () => {
    if (breadcrumb && breadcrumb.length > 0) {
      return (
        <BreadCrumb aria-label="Breadcrumb">
          {breadcrumb.map((item, index) => {
            const isLast = index === breadcrumb.length - 1;
            return (
              <span key={item.label}>
                {index > 0 && <BreadcrumbSeparator aria-hidden="true">/</BreadcrumbSeparator>}
                {isLast || !item.path ? (
                  <BreadcrumbCurrent aria-current={isLast ? 'page' : undefined}>
                    {item.label}
                  </BreadcrumbCurrent>
                ) : (
                  <BreadcrumbLink to={item.path}>{item.label}</BreadcrumbLink>
                )}
              </span>
            );
          })}
        </BreadCrumb>
      );
    }

    return (
      <BreadCrumb aria-label="Breadcrumb">
        {route.map((r, index) =>
          r ? (
            <span key={r}>
              {index > 0 && <BreadcrumbSeparator aria-hidden="true">/</BreadcrumbSeparator>}
              <BreadcrumbCurrent aria-current={index === route.length - 1 ? 'page' : undefined}>
                {r}
              </BreadcrumbCurrent>
            </span>
          ) : null
        )}
      </BreadCrumb>
    );
  };

  return (
    <Background>
      <Container>
        <Link to="/admin-dashboard" aria-label="Ir para Dashboard">
          {minimal ? <LogoIconMinimal /> : <LogoIcon />}
        </Link>

        {renderBreadcrumb()}

        <SearchButton
          onClick={handleSearchClick}
          aria-label="Abrir busca rapida (Ctrl+K)"
          title="Busca rapida"
        >
          <SearchIcon />
          <span>Abrir busca rápida (Ctrl+K)</span>
          <Kbd>Ctrl K</Kbd>
        </SearchButton>

        <NotificationBadge />

        <Profile
          onClick={() => push('/profile')}
          aria-label={`Perfil de ${user?.name || 'usuario'}`}
        >
          <img
            src={avatar}
            alt=""
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><circle cx="12" cy="8" r="4"/><path d="M12 14c-4 0-8 2-8 4v2h16v-2c0-2-4-4-8-4z"/></svg>';
            }}
          />
          <p>{user?.name || 'Usuario'}</p>
        </Profile>

        <Separator />

        <LogoutButton
          onClick={handleLogout}
          onKeyDown={handleLogoutKeyDown}
          aria-label="Sair da conta"
          title="Sair"
          tabIndex={0}
        >
          <SignOutIcon />
        </LogoutButton>
      </Container>
    </Background>
  );
}
